<script>
/* eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text, prettier/prettier -- SiteUP WAHA wizard copy is PT-BR-first; full i18n migration is follow-up */
/* global axios */
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import router from '../../../../index';
import types from 'dashboard/store/mutation-types';

// WhatsApp Business wizard backed by WAHA. Creates a Chatwoot Channel::Api
// inbox, then calls our Waha::SessionService endpoints to spin up a WAHA
// session and display the QR code for scanning.
//
// Steps:
//   1. Name the inbox
//   2. Pick agents + advanced config (ignore flags, lock to single conversation)
//   3. QR code + live status polling → on WORKING, redirect to the inbox

export default {
  name: 'WhatsappWebWaha',

  data() {
    return {
      step: 1, // 1=name, 2=agents+config, 3=qr
      inboxName: 'WhatsApp',
      submitting: false,
      errorMessage: '',

      // Agent selection + config
      agents: [],
      agentsLoading: false,
      selectedAgentIds: [],
      lockSingleConversation: true,
      ignoreStatus: true,
      ignoreGroups: false,
      ignoreChannels: true,
      ignoreBroadcast: true,
      showAgentName: true,
      enableCommands: true,
      markAsRead: true,
      locale: 'pt-BR',

      // Created inbox (after step 2 submits)
      inboxId: null,

      // QR + status
      qrCode: null,
      qrLoading: false,
      qrError: null,
      wahaStatus: null, // WORKING | SCAN_QR_CODE | STARTING | STOPPED | FAILED | ERROR | NOT_CONFIGURED
      statusPollTimer: null,
    };
  },

  computed: {
    ...mapGetters({
      currentAccountId: 'getCurrentAccountId',
    }),

    canContinueStep1() {
      return this.inboxName.trim().length >= 2;
    },

    canCreateInbox() {
      return this.selectedAgentIds.length > 0 && !this.submitting;
    },

    statusLabel() {
      const map = {
        WORKING: 'Conectado',
        SCAN_QR_CODE: 'Aguardando leitura do QR Code',
        STARTING: 'Iniciando sessão',
        STOPPED: 'Parado',
        FAILED: 'Falha',
        ERROR: 'Erro ao consultar',
        NOT_CONFIGURED: 'Não configurado',
      };
      return map[this.wahaStatus] || this.wahaStatus || '—';
    },

    statusColor() {
      if (this.wahaStatus === 'WORKING') return 'bg-green-500';
      if (this.wahaStatus === 'SCAN_QR_CODE') return 'bg-amber-500';
      if (this.wahaStatus === 'STARTING') return 'bg-blue-500';
      if (this.wahaStatus === 'FAILED' || this.wahaStatus === 'ERROR') return 'bg-red-500';
      return 'bg-n-slate-8';
    },

    statusTextColor() {
      if (this.wahaStatus === 'WORKING') return 'text-green-600 dark:text-green-400';
      if (this.wahaStatus === 'SCAN_QR_CODE') return 'text-amber-600 dark:text-amber-400';
      if (this.wahaStatus === 'FAILED' || this.wahaStatus === 'ERROR') return 'text-red-600 dark:text-red-400';
      return 'text-n-slate-11';
    },
  },

  mounted() {
    this.loadAgents();
  },

  beforeUnmount() {
    this.stopStatusPolling();
  },

  methods: {
    // ── Step 1 → 2 ─────────────────────────────────────────────────────────
    goToStep2() {
      if (!this.canContinueStep1) {
        this.errorMessage = 'Dê um nome de pelo menos 2 caracteres';
        return;
      }
      this.errorMessage = '';
      this.step = 2;
    },

    // ── Agents ──────────────────────────────────────────────────────────────
    async loadAgents() {
      this.agentsLoading = true;
      try {
        const { data } = await axios.get(`/api/v1/accounts/${this.currentAccountId}/agents`);
        this.agents = data || [];
      } catch (err) {
        this.errorMessage = 'Erro ao carregar agentes';
      } finally {
        this.agentsLoading = false;
      }
    },

    toggleAgent(agentId) {
      const idx = this.selectedAgentIds.indexOf(agentId);
      if (idx >= 0) this.selectedAgentIds.splice(idx, 1);
      else this.selectedAgentIds.push(agentId);
    },

    // ── Step 2 → 3 ─────────────────────────────────────────────────────────
    async createInboxAndBootstrap() {
      if (!this.canCreateInbox) return;

      this.submitting = true;
      this.errorMessage = '';

      try {
        // 1. Create the Chatwoot inbox as Channel::Api so the public API
        //    (used by WAHA's chatwoot integration to create contacts) finds
        //    the inbox via its polymorphic channel_type column. Creating with
        //    'whatsapp_web' would set channel_type=Channel::WhatsappWeb, and
        //    Public::Api::V1::InboxesController does Channel::Api.find_by!,
        //    so the .inbox association comes back nil and WAHA gets a 500.
        const apiInbox = await this.$store.dispatch('inboxes/createChannel', {
          name: this.inboxName.trim(),
          channel: {
            type: 'api',
            additional_attributes: {},
          },
        });

        this.inboxId = apiInbox.id;

        // 2. Add selected agents to the inbox
        if (this.selectedAgentIds.length > 0) {
          await axios.post(`/api/v1/accounts/${this.currentAccountId}/inbox_members`, {
            inbox_id: apiInbox.id,
            user_ids: this.selectedAgentIds,
          });
        }

        // 3. Bootstrap WAHA session via our new controller
        const { data: wahaData } = await axios.post(
          `/api/v1/accounts/${this.currentAccountId}/inboxes/${apiInbox.id}/waha/create_session`,
          {
            engine: 'GOWS',
            ignore_status: this.ignoreStatus,
            ignore_groups: this.ignoreGroups,
            ignore_channels: this.ignoreChannels,
            ignore_broadcast: this.ignoreBroadcast,
            lock_single_conversation: this.lockSingleConversation,
            show_agent_name: this.showAgentName,
            enable_commands: this.enableCommands,
            mark_as_read: this.markAsRead,
            locale: this.locale,
          }
        );

        if (wahaData?.session_name) {
          this.$store.commit(`inboxes/${types.UPDATE_WAHA_STATUS}`, {
            inboxId: apiInbox.id,
            wahaSessionName: wahaData.session_name,
            wahaStatus: wahaData.status,
            wahaStatusCheckedAt: wahaData.checked_at,
          });
        }

        // 4. Advance to QR step and start polling
        this.step = 3;
        this.startStatusPolling();
        this.fetchQrCode();
      } catch (err) {
        const msg = err?.response?.data?.error || err.message || 'Erro desconhecido';
        this.errorMessage = `Erro ao criar inbox: ${msg}`;
        useAlert(this.errorMessage);
      } finally {
        this.submitting = false;
      }
    },

    // ── QR polling ──────────────────────────────────────────────────────────
    async fetchQrCode() {
      if (!this.inboxId) return;
      this.qrLoading = true;
      this.qrError = null;
      try {
        const { data } = await axios.get(
          `/api/v1/accounts/${this.currentAccountId}/inboxes/${this.inboxId}/waha/qr_code`
        );
        this.qrCode = data.qr_code || null;
        if (data.status) this.wahaStatus = data.status;
      } catch (err) {
        this.qrError = 'Não foi possível gerar o QR Code. Tente reconectar.';
      } finally {
        this.qrLoading = false;
      }
    },

    startStatusPolling() {
      if (this.statusPollTimer) clearInterval(this.statusPollTimer);
      this.statusPollTimer = setInterval(() => this.pollStatus(), 3000);
    },

    stopStatusPolling() {
      if (this.statusPollTimer) {
        clearInterval(this.statusPollTimer);
        this.statusPollTimer = null;
      }
    },

    async pollStatus() {
      if (!this.inboxId) return;
      try {
        const { data } = await axios.get(
          `/api/v1/accounts/${this.currentAccountId}/inboxes/${this.inboxId}/waha/status`
        );
        this.wahaStatus = data.status;

        if (data.status === 'WORKING') {
          this.stopStatusPolling();
          this.onConnected();
        } else if (data.status === 'SCAN_QR_CODE' && !this.qrCode) {
          this.fetchQrCode();
        }
      } catch (err) {
        // silent — keep polling
      }
    },

    async reconnect() {
      if (!this.inboxId) return;
      try {
        this.qrCode = null;
        this.qrLoading = true;
        this.wahaStatus = 'STARTING';
        await axios.post(
          `/api/v1/accounts/${this.currentAccountId}/inboxes/${this.inboxId}/waha/reconnect`
        );
        setTimeout(() => this.fetchQrCode(), 2000);
      } catch (err) {
        this.qrError = 'Erro ao reconectar';
      } finally {
        this.qrLoading = false;
      }
    },

    onConnected() {
      useAlert('WhatsApp conectado com sucesso!');
      setTimeout(() => {
        router.replace({
          name: 'settings_inbox_show',
          params: {
            accountId: this.currentAccountId,
            inboxId: this.inboxId,
          },
        });
      }, 2000);
    },
  },
};
</script>

<template>
  <div class="h-full w-full p-6 col-span-6 overflow-auto">
<!-- ═════════════ Step 1 — Name ═════════════ -->
    <template v-if="step === 1">
      <div class="max-w-2xl mx-auto">
        <h2 class="text-2xl font-extrabold text-n-slate-12 mb-2">
          {{ $t('INBOX_MGMT.ADD.AUTH.CHANNEL.WHATSAPP_WEB.TITLE') }}
        </h2>
        <p class="text-sm text-n-slate-11 mb-2">
          {{ $t('INBOX_MGMT.ADD.AUTH.CHANNEL.WHATSAPP_WEB.DESCRIPTION') }}
        </p>
        <p class="text-sm text-n-slate-11 mb-6">
          {{ $t('INBOX_MGMT.ADD.WHATSAPP_WEB.WAHA_WIZARD.STEP1_DESC') }}
        </p>

        <div class="mb-4">
          <label class="block text-sm font-medium text-n-slate-12 mb-2">
            Nome da Caixa de Entrada
          </label>
          <input
            v-model="inboxName"
            type="text"
            placeholder="Ex: WhatsApp Vendas"
            class="w-full px-4 py-2.5 rounded-xl border border-n-weak bg-n-surface-1 text-n-slate-12 text-sm focus:outline-none focus:border-n-blue-9"
            @keyup.enter="goToStep2"
          />
          <p class="text-xs text-n-slate-11 mt-1.5">
            Esse nome ajuda você e sua equipe a identificar a conexão.
          </p>
        </div>

        <div
          v-if="errorMessage"
          class="mb-4 p-3 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-900/40 text-sm text-red-700 dark:text-red-300"
        >
          {{ errorMessage }}
        </div>

        <div class="flex justify-end gap-2 mt-4">
          <button
            class="px-5 py-2 rounded-xl text-sm font-semibold bg-n-blue-9 text-white hover:bg-n-blue-10 disabled:opacity-50"
            :disabled="!canContinueStep1"
            @click="goToStep2"
          >
            Próximo →
          </button>
        </div>
      </div>
    </template>

    <!-- ═════════════ Step 2 — Agents + Config ═════════════ -->
    <template v-if="step === 2">
      <div class="max-w-2xl mx-auto">
        <h2 class="text-2xl font-extrabold text-n-slate-12 mb-2">
          Agentes e Configurações
        </h2>
        <p class="text-sm text-n-slate-11 mb-6">
          Selecione os agentes que terão acesso à inbox <strong>{{ inboxName }}</strong>.
        </p>

        <!-- Agents list -->
        <div class="mb-6">
          <label class="block text-sm font-medium text-n-slate-12 mb-2">
            Agentes <span class="text-red-500">*</span>
          </label>
          <div
            class="max-h-72 overflow-y-auto border border-n-weak rounded-xl p-3 space-y-1 bg-n-surface-1"
          >
            <div v-if="agentsLoading" class="text-center py-4 text-n-slate-11 text-sm">
              <span class="i-ri-loader-4-line size-4 animate-spin inline-block" />
              Carregando...
            </div>
            <div v-else-if="!agents.length" class="text-center py-4 text-n-slate-11 text-sm">
              Nenhum agente disponível
            </div>
            <label
              v-for="agent in agents"
              :key="agent.id"
              class="flex items-center gap-3 p-2 rounded hover:bg-n-slate-2 dark:hover:bg-n-solid-2 cursor-pointer"
            >
              <input
                type="checkbox"
                :checked="selectedAgentIds.includes(agent.id)"
                class="size-4 accent-n-blue-9"
                @change="toggleAgent(agent.id)"
              />
              <div class="size-8 rounded-full bg-n-slate-3 dark:bg-n-solid-3 flex items-center justify-center text-xs font-bold text-n-slate-12 overflow-hidden">
                <img v-if="agent.thumbnail" :src="agent.thumbnail" class="w-full h-full object-cover" />
                <template v-else>{{ (agent.name[0] || '?').toUpperCase() }}</template>
              </div>
              <div class="flex-1 min-w-0">
                <div class="text-sm text-n-slate-12 truncate font-medium">{{ agent.name }}</div>
                <div class="text-xs text-n-slate-11 truncate">{{ agent.email }}</div>
              </div>
            </label>
          </div>
        </div>

        <!-- Config -->
        <h3 class="text-sm font-bold text-n-slate-12 mb-3">Configurações avançadas</h3>
        <div class="space-y-2 mb-6">
          <!-- NEW: show agent name -->
          <label class="flex items-center justify-between p-3 rounded-xl border border-n-weak bg-n-surface-1 cursor-pointer">
            <div>
              <div class="text-sm font-medium text-n-slate-12">Mostrar nome do atendente</div>
              <div class="text-xs text-n-slate-11">
                Prefixa mensagens com <span class="font-mono bg-n-slate-3 dark:bg-n-solid-3 px-1 rounded">*Nome*:</span> + quebra de linha antes do texto
              </div>
            </div>
            <input v-model="showAgentName" type="checkbox" class="size-5 accent-n-blue-9" />
          </label>

          <!-- NEW: enable commands -->
          <label class="flex items-center justify-between p-3 rounded-xl border border-n-weak bg-n-surface-1 cursor-pointer">
            <div>
              <div class="text-sm font-medium text-n-slate-12">Permitir comandos via WhatsApp</div>
              <div class="text-xs text-n-slate-11">Agentes podem usar <span class="font-mono">/assign</span>, <span class="font-mono">/resolve</span>, etc diretamente do WhatsApp</div>
            </div>
            <input v-model="enableCommands" type="checkbox" class="size-5 accent-n-blue-9" />
          </label>

          <!-- NEW: mark as read -->
          <label class="flex items-center justify-between p-3 rounded-xl border border-n-weak bg-n-surface-1 cursor-pointer">
            <div>
              <div class="text-sm font-medium text-n-slate-12">Marcar como lido automaticamente</div>
              <div class="text-xs text-n-slate-11">Marca mensagens como lidas no WhatsApp quando o agente abre a conversa no sistema</div>
            </div>
            <input v-model="markAsRead" type="checkbox" class="size-5 accent-n-blue-9" />
          </label>

          <!-- NEW: locale -->
          <div class="p-3 rounded-xl border border-n-weak bg-n-surface-1">
            <div class="flex items-center justify-between gap-3">
              <div>
                <div class="text-sm font-medium text-n-slate-12">Idioma da integração</div>
                <div class="text-xs text-n-slate-11">Usado nas mensagens internas do sistema (ex: notificações automáticas)</div>
              </div>
              <select
                v-model="locale"
                class="px-3 py-1.5 rounded-xl border border-n-weak bg-n-surface-1 text-n-slate-12 text-sm focus:outline-none focus:border-n-blue-9"
              >
                <option value="pt-BR">Português (Brasil)</option>
                <option value="en-US">English (US)</option>
              </select>
            </div>
          </div>

          <div class="pt-2 border-t border-n-weak" />

          <label class="flex items-center justify-between p-3 rounded-xl border border-n-weak bg-n-surface-1 cursor-pointer">
            <div>
              <div class="text-sm font-medium text-n-slate-12">Bloquear conversa única</div>
              <div class="text-xs text-n-slate-11">Só 1 conversa ativa por contato — ideal pra atendimento sequencial</div>
            </div>
            <input v-model="lockSingleConversation" type="checkbox" class="size-5 accent-n-blue-9" />
          </label>
          <label class="flex items-center justify-between p-3 rounded-xl border border-n-weak bg-n-surface-1 cursor-pointer">
            <div>
              <div class="text-sm font-medium text-n-slate-12">Ignorar status</div>
              <div class="text-xs text-n-slate-11">Não criar conversas pra stories/status</div>
            </div>
            <input v-model="ignoreStatus" type="checkbox" class="size-5 accent-n-blue-9" />
          </label>
          <label class="flex items-center justify-between p-3 rounded-xl border border-n-weak bg-n-surface-1 cursor-pointer">
            <div>
              <div class="text-sm font-medium text-n-slate-12">Ignorar grupos</div>
              <div class="text-xs text-n-slate-11">Ative se não quer receber mensagens de grupos</div>
            </div>
            <input v-model="ignoreGroups" type="checkbox" class="size-5 accent-n-blue-9" />
          </label>
          <label class="flex items-center justify-between p-3 rounded-xl border border-n-weak bg-n-surface-1 cursor-pointer">
            <div>
              <div class="text-sm font-medium text-n-slate-12">Ignorar canais</div>
              <div class="text-xs text-n-slate-11">Não receber mensagens de canais do WhatsApp</div>
            </div>
            <input v-model="ignoreChannels" type="checkbox" class="size-5 accent-n-blue-9" />
          </label>
          <label class="flex items-center justify-between p-3 rounded-xl border border-n-weak bg-n-surface-1 cursor-pointer">
            <div>
              <div class="text-sm font-medium text-n-slate-12">Ignorar broadcast</div>
              <div class="text-xs text-n-slate-11">Não receber listas de transmissão</div>
            </div>
            <input v-model="ignoreBroadcast" type="checkbox" class="size-5 accent-n-blue-9" />
          </label>
        </div>

        <div
          v-if="errorMessage"
          class="mb-4 p-3 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-900/40 text-sm text-red-700 dark:text-red-300"
        >
          {{ errorMessage }}
        </div>

        <div class="flex justify-between gap-2">
          <button
            class="px-4 py-2 rounded-xl text-sm text-n-slate-11 hover:bg-n-slate-2 dark:hover:bg-n-solid-2"
            :disabled="submitting"
            @click="step = 1"
          >
            ← Voltar
          </button>
          <button
            class="px-5 py-2 rounded-xl text-sm font-semibold bg-n-blue-9 text-white hover:bg-n-blue-10 disabled:opacity-50"
            :disabled="!canCreateInbox"
            @click="createInboxAndBootstrap"
          >
            <span v-if="submitting" class="i-ri-loader-4-line size-4 animate-spin inline-block mr-1" />
            {{ submitting ? 'Criando...' : 'Criar e Conectar' }}
          </button>
        </div>
      </div>
    </template>

    <!-- ═════════════ Step 3 — QR Code + Status ═════════════ -->
    <template v-if="step === 3">
      <div class="max-w-4xl mx-auto">
        <h2 class="text-2xl font-extrabold text-n-slate-12 mb-2">
          Escaneie o QR Code
        </h2>
        <p class="text-sm text-n-slate-11 mb-6">
          Caixa de entrada <strong>{{ inboxName }}</strong> criada. Agora conecte seu WhatsApp.
        </p>
        <p class="text-xs text-n-slate-11 mb-6">
          {{ $t('INBOX_MGMT.ADD.WHATSAPP_WEB.WAHA_WIZARD.QR_SETTINGS_HINT') }}
        </p>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
          <!-- Instructions -->
          <div class="space-y-4">
            <h3 class="text-sm font-bold text-n-slate-12">Como conectar</h3>
            <ol class="space-y-3">
              <li class="flex gap-3">
                <span class="flex-shrink-0 size-6 rounded-full bg-n-blue-9 text-white flex items-center justify-center text-xs font-bold">1</span>
                <span class="text-sm text-n-slate-12 pt-0.5">Abra o <strong>WhatsApp</strong> no celular</span>
              </li>
              <li class="flex gap-3">
                <span class="flex-shrink-0 size-6 rounded-full bg-n-blue-9 text-white flex items-center justify-center text-xs font-bold">2</span>
                <span class="text-sm text-n-slate-12 pt-0.5">Vá em <strong>Configurações → Aparelhos conectados</strong></span>
              </li>
              <li class="flex gap-3">
                <span class="flex-shrink-0 size-6 rounded-full bg-n-blue-9 text-white flex items-center justify-center text-xs font-bold">3</span>
                <span class="text-sm text-n-slate-12 pt-0.5">Toque em <strong>Conectar um aparelho</strong></span>
              </li>
              <li class="flex gap-3">
                <span class="flex-shrink-0 size-6 rounded-full bg-n-blue-9 text-white flex items-center justify-center text-xs font-bold">4</span>
                <span class="text-sm text-n-slate-12 pt-0.5">Aponte a câmera pro QR Code ao lado</span>
              </li>
            </ol>

            <div class="p-3 rounded-xl border border-n-weak bg-n-surface-1 flex items-center gap-3">
              <div class="size-3 rounded-full animate-pulse" :class="statusColor" />
              <span class="text-sm font-medium" :class="statusTextColor">{{ statusLabel }}</span>
            </div>
          </div>

          <!-- QR code panel -->
          <div class="rounded-2xl border border-n-weak bg-n-surface-1 p-6 flex flex-col items-center justify-center min-h-[360px]">
            <div v-if="qrError" class="text-center">
              <span class="i-ri-wifi-off-line size-12 text-red-500 block mx-auto mb-3" />
              <p class="text-sm text-red-600 dark:text-red-400 mb-4">{{ qrError }}</p>
              <button
                class="px-4 py-2 rounded-xl text-xs font-semibold bg-n-blue-9 text-white"
                @click="reconnect"
              >
                Tentar reconectar
              </button>
            </div>

            <div v-else-if="wahaStatus === 'WORKING'" class="text-center">
              <div class="size-20 rounded-full bg-green-500 flex items-center justify-center mx-auto mb-4 shadow-lg">
                <span class="i-ri-check-line size-12 text-white" />
              </div>
              <p class="text-xl font-extrabold text-green-600 dark:text-green-400 mb-1">Conectado!</p>
              <p class="text-sm text-n-slate-11">Redirecionando para a inbox...</p>
            </div>

            <div v-else-if="qrLoading || !qrCode" class="text-center">
              <span class="i-ri-loader-4-line size-12 text-n-blue-9 animate-spin block mx-auto mb-3" />
              <p class="text-sm text-n-slate-11">Gerando QR Code...</p>
            </div>

            <div v-else class="flex flex-col items-center gap-4">
              <img :src="qrCode" alt="QR Code WhatsApp" class="w-64 h-64 rounded-2xl border-4 border-n-weak" />
              <p class="text-xs text-n-slate-11 text-center">QR Code válido por alguns minutos</p>
              <button
                class="px-3 py-1.5 rounded-xl text-xs font-medium text-n-slate-11 border border-n-weak hover:bg-n-slate-3 dark:hover:bg-n-solid-3"
                @click="reconnect"
              >
                <span class="i-ri-refresh-line size-3 inline-block mr-1" />
                Gerar novo QR
              </button>
            </div>
          </div>
        </div>
      </div>
    </template>
</div>
</template>
